import { LoadingOverlayComponent } from '../shared/components/loading-overlay/loading-overlay.component';
import { GlobalStationSearchBarComponent } from '../shared/global-station-search-bar/global-station-search-bar.component';
import {
  EppColumnSettingsDialogComponent,
  EppColumnSettingsDialogData,
  EppColumnSettingsDialogResult,
} from './dialog/epp-column-settings-dialog.component';
import { EppMatrixComponent } from './epp-matrix.component';
import { EppMeteogramComponent } from './epp-meteogram.component';
import { EppSettingsComponent } from './epp-settings/epp-settings.component';
import { EppTableComponent } from './epp-table.component';

import { AuthService } from '../auth/auth.service';
import { LanguageService } from '../shared/services/language.service';
import {
  StationInfo,
  StationListItem,
  StationService,
} from '../shared/station.service';
import { SmwService } from '../smw/smw.service';
import { EppService } from './epp.service';

import { IsNilPipe } from '../pipes/is-nil.pipe';
import { DatexPipe } from '../shared/datex.pipe';
import { HasUserSmwPipe } from './pipes/has-user-smw.pipe';
import { IsSolarpowerStationPipe } from './pipes/is-solar-power-station.pipe';
import { IsWindpowerStationPipe } from './pipes/is-wind-power-station.pipe';
import { ToResolutionKeyPipe } from './pipes/to-resolution-key.pipe';

import { ResetInputMatAutocompleteDirective } from '../shared/directives/reset-input-mat-autocomplete.directive';
import { WmIconDirective } from '../shared/wm-icon.directive';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { isNil } from '../../utils/utils';
import {
  isSolarpowerStation,
  isWindpowerStation,
  toResolutionKey,
} from './constants/constants';
import { TippyDirective } from '@ngneat/helipopper';
import moment from 'moment';
import { Moment } from 'moment';
import { map, Observable, startWith, Subscription, timer } from 'rxjs';

export const MODULES = [
  CommonModule,
  MatSidenavModule,
  MatIconModule,
  MatButtonModule,
  RouterModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatSelectModule,
  MatInputModule,
  MatAutocompleteModule,
  MatTooltipModule,
  MatTabsModule,
  MatCardModule,
  MatDividerModule,
  MatListModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaIconComponent,
  GlobalStationSearchBarComponent,
  EppTableComponent,
  EppMeteogramComponent,
  EppMatrixComponent,
  EppSettingsComponent,
];
export const PIPES = [
  DatexPipe,
  IsNilPipe,
  HasUserSmwPipe,
  ToResolutionKeyPipe,
  IsSolarpowerStationPipe,
  IsWindpowerStationPipe,
];
export const DIRECTIVES = [
  ResetInputMatAutocompleteDirective,
  TippyDirective,
  WmIconDirective,
  CdkDropList,
  CdkDrag,
  CdkDragHandle,
];

@Component({
  selector: 'app-epp',
  templateUrl: './epp.component.html',
  styleUrls: ['./epp.component.scss'],
  styles: [
    `
      app-global-station-search-bar:empty {
        display: none;
      }
    `,
  ],
  imports: [
    ...MODULES,
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES,
    LoadingOverlayComponent,
  ],
})
export class EppComponent implements OnInit, OnDestroy {
  private platform = inject(Platform);
  private breakpointObserver = inject(BreakpointObserver);
  public eppService = inject(EppService);
  public auth = inject(AuthService);
  private smwService = inject(SmwService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private stationService = inject(StationService);
  public languageService = inject(LanguageService);
  private dialog = inject(MatDialog);
  public translate = inject(TranslateService);

  panelForm = this.fb.group({
    stationListItem: this.fb.control<null | StationListItem>(null),
    parameterSet: this.fb.control<null | string>(null),
  });
  stationList = signal<Array<StationListItem>>(undefined);
  stationListFilteredOptions$: Observable<StationListItem[]>;
  selectedStationListItem = signal<StationListItem>(undefined);
  parameterSets = signal([]); // Set on init ["summer", "winter"]
  defaultParametersetDetails = signal({}); // Set on init {summer:[{paramater:...,fe_parameter:..., ...}], winter: [{paramater:...,fe_parameter:..., ...}]}
  userParametersets = signal<Record<string, Record<string, any[]>>>({}); // { "4_24": { summer: [...] }, "6_24": { summer: [...] } }
  settingsParameterset = signal<string>(undefined);
  settingsParameters = signal([]);
  showSettings = signal(false);
  views = signal(['TABLE', 'METEOGRAM']);
  selectedView = signal<string>(undefined);
  viewIcons = { TABLE: 'th', METEOGRAM: 'analytics' };
  neutralColor = signal('green');
  tableParameterChanged = signal(0);
  rebuildMeteogram = signal(0);
  meteogramData = signal<any[]>(undefined);
  showMobileFormFields = signal(false);
  tabsSelectedIndex = signal(0);

  settingsParametersByResolutionAndSet = signal<
    Record<string, Record<string, any[]>>
  >({});

  eppSettings = {
    previousStationId: null,
    previousParameterSet: null,
    view: null,
    parameterSets: null,
  };

  resolutions = signal<any>(undefined); // HOURS FOR TABLE
  selectedResolution = signal<any>(undefined);
  lastIssueTime = signal({});
  eppData = signal<any>(undefined);

  loadingData = signal(false);

  infosForStations: Array<StationInfo>;

  subscriptions: Subscription[] = [];
  windpowerMode = false;
  solarpowerMode = false;

  setEnergyModes() {
    this.windpowerMode = this.parameterSet() === 'energy-wind-power';
    this.solarpowerMode = this.parameterSet() === 'energy-solar-power';
  }

  checkStationForParameterset(stationId: number): boolean {
    if (this.windpowerMode && !isWindpowerStation(stationId)) return false;
    if (this.solarpowerMode && !isSolarpowerStation(stationId)) return false;
    return true;
  }

  private handset = toSignal(
    this.breakpointObserver
      .observe(['(max-width: 900px)'])
      .pipe(map(r => r.matches)),
    { initialValue: this.breakpointObserver.isMatched(['(max-width: 900px)']) }
  );

  readonly isMobilePlatform = computed(
    () => this.handset() || this.platform.ANDROID || this.platform.IOS
  );

  hasBetweenOneAndSixStations = computed(() => {
    const stationList = this.stationList();
    return stationList?.length > 1 && stationList?.length < 6;
  });

  parameterSetValue = computed(() => {});

  hasMoreThanFiveStations = computed(() => {
    return this.stationList()?.length > 5;
  });

  get stationListItem() {
    return this.panelForm.get('stationListItem');
  }

  parameterSet = toSignal(this.panelForm.get('parameterSet').valueChanges, {
    initialValue: this.panelForm.get('parameterSet').value,
  });

  savedEppData = computed(() => {
    return this.eppData()?.[this.parameterSet()]?.[
      this.selectedStationListItem().stationId
    ]?.[this.toResolutionKey(this.selectedResolution())];
  });

  private get eppDataExists() {
    return !isNil(this.savedEppData());
  }

  savedLastIssueTime = computed(() => {
    return this.lastIssueTime()?.[this.parameterSet()]?.[
      this.selectedStationListItem().stationId
    ]?.[this.toResolutionKey(this.selectedResolution())];
  });

  ngOnInit() {
    this.initComponent();
  }

  async initComponent() {
    await this.eppService.getColorMappingNames();
    await this.eppService.getColorMappings();
    this.neutralColor.set(this.eppService.readColorScheme());
    this.readEppSettings();
    this.initParameterSets();
    this.initViews();
    this.initResolutions();
    this.initParameters();
    this.getStationList();
    this.setEnergyModes();
    this.infosForStations = await Promise.all(
      this.getStationsInfo(this.stationList())
    );
    this.stationList.set([
      ...this.addElevationInfoToStationList(
        this.stationList(),
        this.infosForStations
      ),
    ]);
    this.initStations();
    this.listenForStationListFilterChanges();
    /*    this.elevationOfStation = */
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getResolutionKey(resolution: any): string {
    return `${resolution.days}_${resolution.hourly}`;
  }

  private isLegacyParameterSetFormat(value: any): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const keys = Object.keys(value);
    if (keys.length === 0) {
      return false;
    }

    return keys.every(key => Array.isArray(value[key]));
  }

  private getDefaultParametersFor(parameterset: string): any[] {
    return this.toUserParameterset(
      this.defaultParametersetDetails()[parameterset] ?? []
    );
  }

  private normalizeStoredParameterSets(
    stored: any
  ): Record<string, Record<string, any[]>> {
    const next: Record<string, Record<string, any[]>> = {};

    for (const resolution of this.resolutions() ?? []) {
      const resolutionKey = this.getResolutionKey(resolution);
      next[resolutionKey] = {};

      for (const parameterset of this.parameterSets()) {
        const storedResolutionBlock = stored?.[resolutionKey];
        const nestedValue = storedResolutionBlock?.[parameterset];

        if (nestedValue) {
          next[resolutionKey][parameterset] = this.checkForNewParameters(
            parameterset,
            nestedValue.map(entry => ({ ...entry }))
          );
          continue;
        }

        if (this.isLegacyParameterSetFormat(stored) && stored[parameterset]) {
          next[resolutionKey][parameterset] = this.checkForNewParameters(
            parameterset,
            stored[parameterset].map(entry => ({ ...entry }))
          );
          continue;
        }

        next[resolutionKey][parameterset] =
          this.getDefaultParametersFor(parameterset);
      }
    }

    return next;
  }

  getUserParametersForCurrentResolution(): any[] {
    const resolutionKey = this.getResolutionKey(this.selectedResolution());
    return (
      this.userParametersets()?.[resolutionKey]?.[this.parameterSet()] ?? []
    );
  }

  initParameters(): void {
    this.eppService
      .getDefaultParametersetDetails(this.parameterSets())
      .then(details => {
        this.defaultParametersetDetails.set({ ...details });

        this.userParametersets.set(
          this.normalizeStoredParameterSets(this.eppSettings.parameterSets)
        );
      });
  }

  onTableHeaderContextMenu(
    parameter: string,
    resolution: any,
    parameterset: string
  ): void {
    const resolutionKey = this.getResolutionKey(resolution);

    const parameterDefinitions =
      this.defaultParametersetDetails()[parameterset] ?? [];

    const allowedParameters = parameterDefinitions
      .filter(detail => detail.hourly === resolution.hourly)
      .map(detail => detail.fe_parameter)
      .filter(p => p !== 'hidden');

    const currentEntries =
      this.userParametersets()?.[resolutionKey]?.[parameterset] ??
      this.getDefaultParametersFor(parameterset);

    const filteredEntries = currentEntries
      .filter(entry => allowedParameters.includes(entry.parameter))
      .map(entry => ({ ...entry }));

    const resolutionLabel = `${resolution.days} ${this.translate.instant(
      'PRODUCT.EPP.RESOLUTION.DAYS'
    )} (${this.translate.instant(
      `PRODUCT.EPP.RESOLUTION.HOURLY.${resolution.hourly}`
    )})`;

    const data: EppColumnSettingsDialogData & { resolutionLabel: string } = {
      title: `PRODUCT.EPP.PARAMETERSET.SETS.${parameterset}`,
      resolutionLabel,
      entries: filteredEntries,
      fixedColumns: resolution.hourly < 12 ? ['dtg'] : ['dtg', 'dayNight'],
    };

    const dialogRef = this.dialog.open(EppColumnSettingsDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      data,
    });

    dialogRef
      .afterClosed()
      .subscribe((result?: EppColumnSettingsDialogResult) => {
        if (!result?.entries) return;

        const nextEntries = result.entries.map(entry => ({ ...entry }));

        this.userParametersets.update(all => ({
          ...all,
          [resolutionKey]: {
            ...all?.[resolutionKey],
            [parameterset]: nextEntries,
          },
        }));

        this.eppSettings.parameterSets = this.userParametersets();
        this.saveEppSettings();

        this.tableParameterChanged.update(v => v + 1);
        this.rebuildMeteogram.update(v => v + 1);
      });
  }

  /*async initParameters(): Promise<void> {
    this.eppService
      .getDefaultParametersetDetails(this.parameterSets())
      .then(details => {
        this.defaultParametersetDetails.set({ ...details });
        this.userParametersets.set({});
        if (this.eppSettings.parameterSets) {
          for (const parameterset of this.parameterSets()) {
            if (this.eppSettings.parameterSets.hasOwnProperty(parameterset)) {
              this.userParametersets.update(userParametersets => {
                return {
                  ...userParametersets,
                  [parameterset]: this.checkForNewParameters(
                    parameterset,
                    this.eppSettings.parameterSets[parameterset]
                  ),
                };
              });
            } else {
              // ERROR MONITORIZATION
              try {
                this.userParametersets.update(userParametersets => {
                  return {
                    ...userParametersets,
                    [parameterset]: this.toUserParameterset(
                      this.defaultParametersetDetails()[parameterset]
                    ),
                  };
                });
              } catch (error) {
                rg4js('send', {
                  error,
                  customData: {
                    defaultParametersetDetails: JSON.stringify(
                      this.defaultParametersetDetails()
                    ),
                    parameterset,
                  },
                });
                throw error;
              }
            }
          }
        } else {
          for (const parameterset of this.parameterSets()) {
            // ERROR MONITORIZATION
            try {
              this.userParametersets.update(userParametersets => {
                return {
                  ...userParametersets,
                  [parameterset]: this.toUserParameterset(
                    this.defaultParametersetDetails()[parameterset]
                  ),
                };
              });
            } catch (error) {
              rg4js('send', {
                error,
                customData: {
                  defaultParametersetDetails: JSON.stringify(
                    this.defaultParametersetDetails()
                  ),
                  parameterset,
                },
              });
              throw error;
            }
          }
        }
      });
  }*/

  displayStationName(station: StationListItem): string {
    const stationElevation = station?.elevation;
    const overwriteStationName = station?.overwriteStationName;

    let result = '';
    if (!isNil(overwriteStationName)) result = overwriteStationName;
    if (!isNil(stationElevation)) result += ` | ${stationElevation} m`;
    return result;
  }

  checkForNewParameters(parameterset: string, parameters: Array<any>): any {
    const checkedParameters = parameters;
    for (const detail of this.defaultParametersetDetails()[parameterset] ??
      []) {
      if (
        !parameters.some(p => p.parameter === detail.fe_parameter) &&
        detail.fe_parameter !== 'hidden'
      ) {
        checkedParameters.push({
          parameter: detail.fe_parameter,
          selected: detail.standard,
        });
      }
    }
    return checkedParameters;
  }

  toUserParameterset(parametersetDetails: any): any {
    const userParameterset = [];
    for (const detail of parametersetDetails ?? []) {
      if (
        !userParameterset.some(
          parameterSet => parameterSet.parameter === detail.fe_parameter
        ) &&
        detail.fe_parameter !== 'hidden'
      ) {
        userParameterset.push({
          parameter: detail.fe_parameter,
          selected: detail.standard,
        });
      }
    }
    return userParameterset;
  }

  getStationList(): void {
    this.stationList.set([
      ...this.eppService
        .getEppStations()
        .sort((a, b) =>
          a.overwriteStationName > b.overwriteStationName ? 1 : -1
        ),
    ]);
  }

  initStations(): void {
    if (this.stationList().length > 0) {
      this.route.params.subscribe(params => {
        if (params && params.hasOwnProperty('station')) {
          const stationId = params['station'];
          const currentStation = this.findStationById(stationId);
          if (!isNil(currentStation)) {
            this.setStationListItem(currentStation);
            this.eppSettings.previousStationId = stationId;
            this.saveEppSettings();
            if (this.selectedResolution() && this.parameterSets()) {
              if (!this.eppDataExists) {
                this.autoRefreshEppData();
              } else {
                this.setMeteogramData();
              }
            }
          }
        } else if (!isNil(this.selectedStationListItem())) {
          this.router.navigate([
            '/epp',
            this.selectedStationListItem().stationId,
          ]);
        } else if (
          this.eppSettings.previousStationId &&
          this.stationList().filter(
            s => s.stationId == this.eppSettings.previousStationId
          ).length > 0
        ) {
          const matchedStations = this.stationList().filter(
            s => s.stationId == this.eppSettings.previousStationId
          );
          if (matchedStations.length > 0) {
            const stationId = matchedStations[0].stationId;
            this.router.navigate(['/epp', stationId]);
          }
        } else if (this.stationList().length > 0) {
          const stationId = this.stationList()[0].stationId;
          this.router.navigate(['/epp', stationId]);
        }
      });
    }
  }

  initParameterSets(): void {
    this.parameterSets.set([...this.eppService.getParameterSets()]);
    if (this.parameterSets().length > 0) {
      if (this.eppSettings.previousParameterSet) {
        if (
          this.parameterSets().some(
            p => p == this.eppSettings.previousParameterSet
          )
        ) {
          this.panelForm.patchValue({
            parameterSet: this.eppSettings.previousParameterSet,
          });
          return;
        }
      }
      this.panelForm.patchValue({
        parameterSet: this.parameterSets()[0],
      });
      this.eppSettings.previousParameterSet = this.parameterSet();
      this.saveEppSettings();
    }
  }

  getSettingsParameters(resolution: any, parameterset: string): any[] {
    const resolutionKey = this.getResolutionKey(resolution);
    return (
      this.settingsParametersByResolutionAndSet()?.[resolutionKey]?.[
        parameterset
      ] ?? []
    );
  }

  hasSettingsParameters(resolution: any, parameterset: string): boolean {
    return this.getSettingsParameters(resolution, parameterset).length > 0;
  }

  onTableColumnOrderChanged(
    orderedParameters: string[],
    resolution: any,
    parameterset: string
  ): void {
    const resolutionKey = this.getResolutionKey(resolution);

    const current = [
      ...(this.settingsParametersByResolutionAndSet()?.[resolutionKey]?.[
        parameterset
      ] ??
        this.userParametersets()?.[resolutionKey]?.[parameterset] ??
        []),
    ];

    const byName = new Map(current.map(entry => [entry.parameter, entry]));

    const reorderedSelected = orderedParameters.map(parameter => {
      const existing = byName.get(parameter);
      return existing ? { ...existing } : { parameter, selected: true };
    });

    const hiddenEntries = current.filter(
      entry => !orderedParameters.includes(entry.parameter)
    );

    const reordered = [...reorderedSelected, ...hiddenEntries];

    this.settingsParametersByResolutionAndSet.update(settings => ({
      ...settings,
      [resolutionKey]: {
        ...settings?.[resolutionKey],
        [parameterset]: reordered,
      },
    }));

    this.userParametersets.update(all => ({
      ...all,
      [resolutionKey]: {
        ...all?.[resolutionKey],
        [parameterset]: reordered,
      },
    }));

    this.eppSettings.parameterSets = this.userParametersets();
    this.saveEppSettings();

    this.tableParameterChanged.update(value => value + 1);
    this.rebuildMeteogram.update(value => value + 1);
  }

  getSelectedParametersCountForResolutionAndSet(
    resolution: any,
    parameterset: string
  ): number {
    return this.getSettingsParameters(resolution, parameterset).filter(
      entry => entry.selected
    ).length;
  }

  getTotalParametersCountForResolutionAndSet(
    resolution: any,
    parameterset: string
  ): number {
    return this.getSettingsParameters(resolution, parameterset).length;
  }

  initViews(): void {
    if (this.eppSettings.view) {
      this.selectedView.set(this.eppSettings.view);
    } else {
      this.selectedView.set(this.views()[0]);
    }
  }

  initResolutions(): void {
    this.resolutions.set([...this.eppService.getResolutions()]);
    if (this.resolutions().length > 0) {
      const resolution = this.resolutions()[0];
      this.selectedResolution.set(
        isNil(resolution) ? undefined : { ...resolution }
      );
    }
  }

  getEppData(reload = false, showSpinner = true): void {
    this.loadingData.set(showSpinner);

    if (!reload) {
      this.meteogramData.set(null);
    } else {
      this.eppData.set(null);
      this.lastIssueTime.set(null);
    }
    this.eppService
      .getEppData(
        this.parameterSet(),
        this.selectedStationListItem().stationId,
        this.selectedResolution().days,
        this.selectedResolution().hourly
      )
      .then(eppResponse => {
        const actualLastIssueTime = moment(eppResponse['last_issue_time']);

        if (!actualLastIssueTime.isSame(this.savedLastIssueTime())) {
          this.setLastIssueTime(actualLastIssueTime);

          this.initializeEppData();

          this.setEppData(eppResponse.forecast);
        }
        this.loadingData.set(false);
      })
      .catch(() => this.loadingData.set(false));
  }

  setLastIssueTime(lastIssueTime: Moment): void {
    const resolution = this.toResolutionKey(this.selectedResolution());
    this.initializeLastIssueTime();

    this.lastIssueTime.update(currentLastIssueTime => {
      currentLastIssueTime[this.parameterSet()][
        this.selectedStationListItem().stationId
      ][resolution] = lastIssueTime;
      return { ...currentLastIssueTime };
    });
  }

  cleanUpEppData(data: Array<any>): Array<any> {
    const cleanedData = [];
    const hourly = this.selectedResolution().hourly;
    const days = this.selectedResolution().days;
    const startDtg = moment();
    const endDtg = moment().add(days, 'days');
    let previousDay;
    for (const dataset of data) {
      const dtg = moment(dataset['valid_at']).utc();
      const dtgLocale = moment(dataset['valid_at']);
      if (hourly === 12) {
        if (dtg.hours() === 6 || dtg.hours() === 18) {
          dataset.dayNight = dtg.hours() === 6 ? 'moon' : 'sun';
          cleanedData.push(dataset);
        }
      } else {
        if (dtg.isSameOrAfter(startDtg) && dtg.isSameOrBefore(endDtg)) {
          if (
            cleanedData.length === 0 ||
            (previousDay && previousDay.dayOfYear() !== dtgLocale.dayOfYear())
          ) {
            cleanedData.push({ date: moment(dtg).local(), isGroupBy: true });
          }
          if (dtg.hours() % hourly === 0) {
            cleanedData.push(dataset);
          }
        }
      }
      previousDay = dtgLocale;
    }
    return cleanedData;
  }

  toResolutionKey(resolution: any): string {
    return toResolutionKey(resolution);
  }

  saveEppSettings(): void {
    localStorage.setItem('eppSettings', JSON.stringify(this.eppSettings));
    const userProfile = this.auth.getUserProfile();

    if (userProfile && userProfile['metadata']['global']) {
      const global = userProfile['metadata']['global'];

      global['epp'] = this.eppSettings;
      global['eopTableColorScheme'] = this.neutralColor();

      this.auth.updateUserData('global', global);
    }
  }

  readEppSettings(): void {
    const storedSettings = localStorage.getItem('eppSettings');
    let settings;
    if (storedSettings) {
      settings = JSON.parse(storedSettings);
    } else {
      const userProfile = this.auth.getUserProfile();
      const global = userProfile['metadata']['global'];
      if (global && global.hasOwnProperty('epp')) {
        settings = global['epp'];
      }
    }

    if (settings) {
      for (const key of Object.keys(this.eppSettings)) {
        if (settings.hasOwnProperty(key)) {
          this.eppSettings[key] = settings[key];
        }
      }

      if (
        this.eppSettings.parameterSets &&
        this.isLegacyParameterSetFormat(this.eppSettings.parameterSets)
      ) {
        this.eppSettings.parameterSets = this.normalizeStoredParameterSets(
          this.eppSettings.parameterSets
        );
        this.saveEppSettings();
      }
    }
  }

  onTabChanged(event: MatTabChangeEvent): void {
    this.tabsSelectedIndex.set(event.index);
    if (event.index < this.resolutions().length) {
      this.meteogramData.set(null);
      const resolution = this.resolutions()[event.index];
      this.selectedResolution.set(
        isNil(resolution) ? undefined : { ...resolution }
      );
      if (!this.eppDataExists) {
        this.getEppData();
      } else {
        this.loadingData.set(true);
        setTimeout(() => {
          this.setMeteogramData();
          this.loadingData.set(false);
        }, 300);
      }
    } else {
      if (this.hasUserSmw(this.selectedStationListItem().stationId)) {
        this.onSmw();
      }
    }
  }

  onStationChange(event: MatSelectChange | MatAutocompleteSelectedEvent): void {
    let stationId;
    if (event instanceof MatSelectChange) {
      stationId = event.source.value['stationId'];
      this.selectedStationListItem.set({ ...event.source.value });
    } else {
      stationId = event.option.value['stationId'];
      this.selectedStationListItem.set({ ...event.option.value });
    }
    this.router.navigate(['/epp', stationId]);
  }

  onGlobalStationSelect(stationName, stationId, stationElevation): void {
    if (this.stationList().filter(s => s.stationId == stationId).length === 0) {
      this.stationList.update(stationList => {
        return [
          ...stationList,
          {
            stationId,
            overwriteStationName: stationName,
            elevation: stationElevation,
          },
        ];
      });
    }
    this.router.navigate(['/epp', stationId]);
  }

  onParameterSetChange(event: MatSelectChange): void {
    const selectedParameterSet = event.source.value;
    this.eppSettings.previousParameterSet = selectedParameterSet;
    this.setEnergyModes();
    this.saveEppSettings();
    if (!this.eppDataExists) {
      this.getEppData();
    } else {
      this.setMeteogramData();
    }
  }

  onViewChange(view: string): void {
    this.selectedView.set(view);
    this.eppSettings.view = this.selectedView();
    this.saveEppSettings();
  }

  onToggleColor(isGreen: boolean) {
    const newColor = isGreen ? 'green' : 'white';
    this.onChangeNeutralColor(newColor);
  }

  onChangeNeutralColor(color: string): void {
    if (color !== this.neutralColor()) {
      this.neutralColor.set(color);
      this.eppService.setGreenTableDesign(this.neutralColor() === 'green');
      this.smwService.greenTableDesign.set(this.neutralColor() === 'green');
      localStorage.setItem('eopTableColorScheme', color);
      const userProfile = this.auth.getUserProfile();
      if (userProfile) {
        if (!userProfile['metadata']['global']) {
          userProfile['metadata']['global'] = {};
        }
        const global = userProfile['metadata']['global'];
        global['eopTableColorScheme'] = this.neutralColor();
        this.auth.updateUserData('global', global);
      }
    }
  }

  onSetupSettings(): void {
    const nextState: Record<string, Record<string, any[]>> = {};

    for (const resolution of this.resolutions() ?? []) {
      const resolutionKey = this.getResolutionKey(resolution);
      nextState[resolutionKey] = {};

      for (const parameterset of this.parameterSets()) {
        const parameterDefinitions =
          this.defaultParametersetDetails()[parameterset] ?? [];

        const allowedParameters = parameterDefinitions
          .filter(detail => detail.hourly === resolution.hourly)
          .map(detail => detail.fe_parameter)
          .filter(parameter => parameter !== 'hidden');

        const source =
          this.userParametersets()?.[resolutionKey]?.[parameterset] ??
          this.getDefaultParametersFor(parameterset);

        nextState[resolutionKey][parameterset] = source
          .filter(entry => allowedParameters.includes(entry.parameter))
          .map(entry => ({ ...entry }));
      }
    }

    this.settingsParametersByResolutionAndSet.set(nextState);
    this.showSettings.set(true);
  }

  onDrop(
    event: CdkDragDrop<any[]>,
    resolution: any,
    parameterset: string
  ): void {
    const newArray = [...event.container.data];
    moveItemInArray(newArray, event.previousIndex, event.currentIndex);

    const resolutionKey = this.getResolutionKey(resolution);

    this.settingsParametersByResolutionAndSet.update(settings => ({
      ...settings,
      [resolutionKey]: {
        ...settings?.[resolutionKey],
        [parameterset]: newArray,
      },
    }));
  }

  onParameterSelectionChange(
    event: MatSelectionListChange,
    resolution: any,
    parameterset: string
  ): void {
    const selectedParameter = event.options[0].value;
    const resolutionKey = this.getResolutionKey(resolution);

    this.settingsParametersByResolutionAndSet.update(settings => {
      const entries = [...(settings?.[resolutionKey]?.[parameterset] ?? [])];
      const index = entries.findIndex(
        entry => entry.parameter === selectedParameter
      );

      if (index >= 0) {
        entries[index] = {
          ...entries[index],
          selected: event.options[0].selected,
        };
      }

      return {
        ...settings,
        [resolutionKey]: {
          ...settings?.[resolutionKey],
          [parameterset]: entries,
        },
      };
    });
  }

  isAllSelected(resolution: any, parameterset: string): boolean {
    const params = this.getSettingsParameters(resolution, parameterset);
    return params.length > 0 && params.every(p => p.selected);
  }

  isSomeSelected(resolution: any, parameterset: string): boolean {
    const params = this.getSettingsParameters(resolution, parameterset);
    const selectedCount = params.filter(p => p.selected).length;
    return selectedCount > 0 && selectedCount < params.length;
  }

  getSelectedParametersCount(parameterset: string): number {
    const resolutionKey = this.getResolutionKey(this.selectedResolution());
    return (
      this.userParametersets()?.[resolutionKey]?.[parameterset] ?? []
    ).filter(parameter => parameter.selected).length;
  }

  getTotalParametersCount(parameterset: string): number {
    const resolutionKey = this.getResolutionKey(this.selectedResolution());
    return (
      this.userParametersets()?.[resolutionKey]?.[parameterset]?.length ?? 0
    );
  }

  toggleSelectAll(
    checked: boolean,
    resolution: any,
    parameterset: string
  ): void {
    const resolutionKey = this.getResolutionKey(resolution);

    this.settingsParametersByResolutionAndSet.update(settings => {
      const entries = [
        ...(settings?.[resolutionKey]?.[parameterset] ?? []),
      ].map(entry => ({
        ...entry,
        selected: checked,
      }));

      return {
        ...settings,
        [resolutionKey]: {
          ...settings?.[resolutionKey],
          [parameterset]: entries,
        },
      };
    });
  }

  onSaveSettings(): void {
    const settings = this.settingsParametersByResolutionAndSet();
    const nextUserParametersetsByResolution: Record<
      string,
      Record<string, any[]>
    > = {};

    for (const resolution of this.resolutions() ?? []) {
      const resolutionKey = this.getResolutionKey(resolution);
      nextUserParametersetsByResolution[resolutionKey] = {};

      for (const parameterset of this.parameterSets()) {
        nextUserParametersetsByResolution[resolutionKey][parameterset] = [
          ...(settings?.[resolutionKey]?.[parameterset] ?? []),
        ].map(entry => ({ ...entry }));
      }
    }

    this.userParametersets.set(nextUserParametersetsByResolution);
    this.eppSettings.parameterSets = nextUserParametersetsByResolution;
    this.saveEppSettings();
    this.showSettings.set(false);

    this.tableParameterChanged.update(value => value + 1);
    this.rebuildMeteogram.update(value => value + 1);
  }

  onCancelSettings(): void {
    this.showSettings.set(false);
  }

  onRestoreStandardSettings(): void {
    const nextState: Record<string, Record<string, any[]>> = {};

    for (const resolution of this.resolutions() ?? []) {
      const resolutionKey = this.getResolutionKey(resolution);
      nextState[resolutionKey] = {};

      for (const parameterset of this.parameterSets()) {
        const defaults = this.toUserParameterset(
          this.defaultParametersetDetails()[parameterset]
        );

        const allowedParameters = (
          this.defaultParametersetDetails()[parameterset] ?? []
        )
          .filter(detail => detail.hourly === resolution.hourly)
          .map(detail => detail.fe_parameter)
          .filter(parameter => parameter !== 'hidden');

        nextState[resolutionKey][parameterset] = defaults
          .filter(entry => allowedParameters.includes(entry.parameter))
          .map(entry => ({ ...entry }));
      }
    }

    this.settingsParametersByResolutionAndSet.set(nextState);
  }

  onResetSettings(): void {
    // parametersets
    for (const parameterset of this.parameterSets()) {
      this.userParametersets.update(userParametersets => {
        return {
          ...userParametersets,
          [parameterset]: this.toUserParameterset(
            this.defaultParametersetDetails()[parameterset]
          ),
        };
      });
    }
    this.eppSettings.parameterSets = this.userParametersets();
    // grün
    if (!this.eppService.getGreenTableDesign()) {
      this.onChangeNeutralColor('green');
    }
    this.saveEppSettings();
    this.showSettings.set(false);
    this.tableParameterChanged.update(
      tableParameterChanged => tableParameterChanged + 1
    );
    this.rebuildMeteogram.update(rebuildMeteogram => rebuildMeteogram + 1);
  }

  onRefreshEpp(): void {
    this.getEppData(true);
  }

  onSmw(): void {
    this.router.navigate(['/smw', this.selectedStationListItem().stationId]);
  }

  hasUserSmw(stationId): boolean {
    if (this.auth.isProductActive('smwProducts')) {
      const customerDocument = this.auth.getCustomerDocument();
      for (const smwProduct of customerDocument['smwProducts']) {
        if (this.auth.isProductActive('smwProducts', smwProduct.id)) {
          for (const station of smwProduct.stations) {
            if (station.stationId == stationId) {
              if (
                this.auth.isUserAllowedToAccessToProduct(
                  smwProduct.id,
                  stationId,
                  true
                )
              ) {
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  }

  getStationsInfo(
    stationList: Array<StationListItem>
  ): Array<Promise<StationInfo>> {
    // requests additional Information of station (e.g. elevation)
    const allPromisesForStationData = [];

    stationList.forEach(stationListItem => {
      const stationInformation$ = this.stationService.getStationInfo(
        stationListItem.stationId + ''
      );

      allPromisesForStationData.push(
        new Promise((resolve, reject) => {
          stationInformation$.subscribe(
            data2 => {
              resolve(data2);
            },
            error => {
              console.log('ERROR:', error);
              // needed to be able to continue even when there was an error,
              // null will be handled when trying to combine elevation & station in addElevationInfoToStationList()
              resolve(null);
            }
          );
        })
      );
    });
    return allPromisesForStationData;
  }

  addElevationInfoToStationList(
    stationList: Array<StationListItem>,
    infosForStations: Array<StationInfo>
  ): Array<StationListItem> {
    if (
      !Array.isArray(stationList) ||
      !stationList.length ||
      !Array.isArray(infosForStations) ||
      !infosForStations.length
    ) {
      return stationList;
    }

    return stationList.map(station => {
      const stationInformation = infosForStations.find(stationInfo => {
        // null means there was an error with receiving the stationInformation data
        // in this case: dont try to find matching station & stationInfo
        if (stationInfo !== null) {
          return station.stationId == stationInfo.stationId;
        }
      });

      if (stationInformation) {
        station['elevation'] = stationInformation.elevation;
      }

      return station;
    });
  }

  private setEppData(forecast) {
    this.eppData.update(eppData => {
      return {
        ...eppData,
        [this.parameterSet()]: {
          ...eppData[this.parameterSet()],
          [this.selectedStationListItem().stationId]: {
            ...eppData[this.parameterSet()][
              this.selectedStationListItem().stationId
            ],
            [this.toResolutionKey(this.selectedResolution())]:
              this.cleanUpEppData(forecast),
          },
        },
      };
    });
    this.setMeteogramData();
  }

  private setMeteogramData() {
    this.meteogramData.set([...(this.savedEppData() ?? [])]);
    this.rebuildMeteogram.update(counter => counter + 1);
  }

  private findStationById(stationId) {
    return this.stationList()?.find(s => s.stationId == stationId);
  }

  private autoRefreshEppData() {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach(s => s.unsubscribe());
    }
    const autoRefreshEppDataSubscription = timer(0, 3600000).subscribe(() => {
      this.getEppData(false, false);
    });
    this.subscriptions.push(autoRefreshEppDataSubscription);
  }

  private setStationListItem(currentStation: StationListItem) {
    this.panelForm.patchValue({
      stationListItem: currentStation,
    });
    this.selectedStationListItem.set({ ...currentStation });
  }

  private listenForStationListFilterChanges() {
    this.stationListFilteredOptions$ = this.stationListItem.valueChanges.pipe(
      startWith(''),
      map(station => {
        const displayingStationName =
          typeof station === 'string'
            ? station
            : this.displayStationName(station);
        return displayingStationName
          ? this.filterStationsByOverwriteStationName(displayingStationName)
          : this.stationList().slice();
      })
    );
  }

  private filterStationsByOverwriteStationName(
    displayingStationName: string
  ): StationListItem[] {
    const filterValue = displayingStationName.toLowerCase();

    return this.stationList().filter(station =>
      this.displayStationName(station).toLowerCase().includes(filterValue)
    );
  }

  private initializeEppData() {
    if (isNil(this.eppData())) {
      this.eppData.set({});
    }

    if (isNil(this.eppData()[this.parameterSet()])) {
      this.eppData.update(eppData => {
        eppData[this.parameterSet()] = {};
        return { ...eppData };
      });
    }

    if (
      isNil(
        this.eppData()[this.parameterSet()][
          this.selectedStationListItem().stationId
        ]
      )
    ) {
      this.eppData.update(eppData => {
        eppData[this.parameterSet()][this.selectedStationListItem().stationId] =
          {};
        return { ...eppData };
      });
    }
  }

  private initializeLastIssueTime() {
    if (isNil(this.lastIssueTime())) {
      this.lastIssueTime.set({});
    }

    if (isNil(this.lastIssueTime()[this.parameterSet()])) {
      this.lastIssueTime.update(lastIssueTime => {
        lastIssueTime[this.parameterSet()] = {};
        return { ...lastIssueTime };
      });
    }
    if (
      isNil(
        this.lastIssueTime()[this.parameterSet()][
          this.selectedStationListItem().stationId
        ]
      )
    ) {
      this.lastIssueTime.update(lastIssueTime => {
        lastIssueTime[this.parameterSet()][
          this.selectedStationListItem().stationId
        ] = {};
        return { ...lastIssueTime };
      });
    }
  }
}
